import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Edit, Trash2, UserCheck, UserX, Mail, Shield, UserPlus, Globe } from 'lucide-react';
import Button from '../components/UI/Button';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { User } from '../types';

interface AdminUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'super_admin' | 'admin' | 'sub_admin';
  permissions: string[];
  flavourIds?: number[];
}

interface AdminUser extends User {
  role: 'super_admin' | 'admin' | 'sub_admin';
  permissions: string[];
  lastLoginAt?: Date;
  isActive: boolean;
  flavourIds?: number[];
  createdBy?: number; // ID of the user who created this admin
  flavours?: {
    id: number;
    name: string;
    slug: string;
    status: string;
  }[];
}

const AdminUsers: React.FC = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'super_admin' | 'admin' | 'sub_admin'>('all');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof AdminUser>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [availableApps, setAvailableApps] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [totalAdminUsers, setTotalAdminUsers] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AdminUserFormData>({
    defaultValues: {
      flavourIds: [],
      permissions: []
    }
  });

  const watchedRole = watch('role');

  // Fetch admin users
  const fetchAdminUsers = async (page: number = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getAdminUsers(page, rowsPerPage);

      setAdminUsers(response.users || []);
      setTotalAdminUsers(response.total || 0);
      setCurrentPage(response.page || page);
    } catch (error) {
      setError('Failed to load admin users');
      toast.error('Failed to load admin users');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available apps for assignment
  const fetchAvailableApps = async () => {
    try {
      const response = await apiService.getAllFlavours();
      setAvailableApps(response || []);
    } catch (error) {
      // ignore app fetch failure
    }
  };



  // Fetch current user to determine role
  const fetchCurrentUser = async () => {
    try {
      const user = await apiService.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      // ignore user fetch failure
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        fetchCurrentUser(),
        fetchAdminUsers(1),
        fetchAvailableApps()
      ]);
    };
    
    initializeData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterName, filterEmail, filterRole]);

  // Role-based access control flags
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin';
  const isSubAdmin = currentUser?.role === 'sub_admin';
  const canManageAdminUsers = isSuperAdmin || isAdmin;
  const canCreateUsers = isSuperAdmin || isAdmin;

  // Multi-tenant filtering - users only see admin users they created or are responsible for
  const getVisibleAdminUsers = () => {
    if (!currentUser) return [];

    if (isSuperAdmin) {
      // Super admin sees all admin users (his customers) + all sub admins across the platform
      return adminUsers.filter(user =>
        user.role === 'admin' || // All admin users (his customers)
        user.role === 'sub_admin' // All sub admins across all admins
      );
    } else if (isAdmin) {
      // Admin sees only their own profile + sub admins they created (their team)
      // Since backend already filters correctly, just return all users for Admin
      // The backend ensures Admin only sees their own profile + sub admins they created
      return adminUsers;
    } else if (isSubAdmin) {
      // Sub admin sees only their own profile
      return adminUsers.filter(user => user.id === currentUser.id);
    }

    return [];
  };

  // Filter visible admin users based on search criteria
  const filteredUsers = getVisibleAdminUsers().filter(user => {
    const matchesName = !filterName || 
      `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(filterName.toLowerCase());
    const matchesEmail = !filterEmail || user.email.toLowerCase().includes(filterEmail.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    return matchesName && matchesEmail && matchesRole;
  });

  // Get current data - filtered by multi-tenant rules
  const currentData = filteredUsers;
  
  // Available roles based on current user's role
  const getAvailableRoles = () => {
    if (isSuperAdmin) {
      return ['admin']; // Super admin can only create customer (admin) users
    } else if (isAdmin) {
      return ['sub_admin']; // Admin can only create team member (sub-admin) users
    }
    return []; // Sub admin cannot create any users
  };

  // Available permissions (only for sub admin users)
  const availablePermissions = [
    { id: 'users_manage', label: 'Manage App Users' },
    { id: 'tours_manage', label: 'Manage Tours' },
    { id: 'apps_manage', label: 'Manage Apps' },
    { id: 'payments_view', label: 'View Payments' },
    { id: 'payments_manage', label: 'Manage Payments' },
    { id: 'reviews_manage', label: 'Manage Reviews' },
    { id: 'analytics_view', label: 'View Analytics' },
    { id: 'settings_manage', label: 'Manage Settings' },
    { id: 'admin_users_manage', label: 'Manage Admin Users' },
  ];

  // Sort users (handle both AdminUser and User types)
  const sortedUsers = [...currentData].sort((a, b) => {
    const aValue = a[sortColumn as keyof typeof a];
    const bValue = b[sortColumn as keyof typeof b];
    
    if (aValue === undefined || bValue === undefined) return 0;
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(totalAdminUsers / rowsPerPage));

  const handleSort = (column: keyof AdminUser) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleView = (user: AdminUser) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleEdit = (user: AdminUser) => {
    setSelectedUser(user);
    setValue('firstName', user.firstName || '');
    setValue('lastName', user.lastName || '');
    setValue('email', user.email);
    setValue('phone', user.phone || '');
    setValue('role', user.role);
    setValue('permissions', user.permissions || []);
    setValue('flavourIds', user.flavourIds || []);
    setIsEditModalOpen(true);
  };

  const handleCreate = () => {
    reset();
    setIsCreateModalOpen(true);
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this admin user? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteAdminUser(userId);
      toast.success('Admin user deleted successfully!');
      await fetchAdminUsers(currentPage);
    } catch (error) {
      toast.error('Failed to delete admin user');
    }
  };

  
  const handleToggleActive = async (userId: number, isActive: boolean) => {
    try {
      await apiService.updateAdminUserStatus(userId, { isActive: !isActive });
      toast.success(`Admin user ${isActive ? 'deactivated' : 'activated'} successfully!`);
      await fetchAdminUsers(currentPage);
    } catch (error) {
      toast.error(`Failed to ${isActive ? 'deactivate' : 'activate'} admin user`);
    }
  };

  const onSubmitCreate = async (data: AdminUserFormData) => {
    try {
      await apiService.createAdminUser(data);
      toast.success('Admin user created successfully!');
      setIsCreateModalOpen(false);
      reset();
      await fetchAdminUsers(currentPage);
    } catch (error) {
      toast.error('Failed to create admin user');
    }
  };

  const onSubmitEdit = async (data: AdminUserFormData) => {
    if (!selectedUser) return;

    try {
      await apiService.updateAdminUser(selectedUser.id, data);
      toast.success('Admin user updated successfully!');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      reset();
      await fetchAdminUsers(currentPage);
    } catch (error) {
      toast.error('Failed to update admin user');
    }
  };

  const columns = [
    {
      key: 'name' as keyof AdminUser,
      header: 'Name',
      sortable: true,
      render: (value: any, user: AdminUser) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-sm font-medium text-white">
              {user.firstName?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 max-w-[200px] truncate">
              {user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.firstName || 'N/A'
              }
            </p>
            <p className="text-sm text-gray-500 max-w-[200px] truncate">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role' as keyof AdminUser,
      header: 'Role',
      sortable: true,
      render: (value: any, user: AdminUser) => (
        <div className="flex items-center space-x-2">
          <Shield className="h-4 w-4 text-gray-400" />
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            user.role === 'super_admin' 
              ? 'bg-red-100 text-red-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {user.role === 'super_admin' ? 'Super Admin' : 
             user.role === 'admin' ? 'Admin' : 'Sub Admin'}
          </span>
        </div>
      ),
    },
    {
      key: 'assignedApps' as keyof AdminUser,
      header: 'Assigned Apps',
      sortable: false,
      render: (value: any, user: AdminUser) => {
        const assignedApps = availableApps.filter(app => 
          user.flavourIds?.includes(app.id)
        );
        return (
          <div className="flex items-start space-x-2">
            <Globe className="h-4 w-4 text-gray-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              {assignedApps.length > 0 ? (
                <div className="space-y-1">
                  {assignedApps.slice(0, 2).map(app => (
                    <div
                      key={app.id}
                      title={`${app.name} (${app.packageName})`}
                      className="text-sm text-gray-700 max-w-[280px] truncate"
                    >
                      <span className="font-medium">{app.name}</span>
                      <span className="text-xs text-gray-500 ml-1">({app.packageName})</span>
                    </div>
                  ))}
                  {assignedApps.length > 2 && (
                    <div className="text-xs text-gray-500">
                      +{assignedApps.length - 2} more apps
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-sm text-gray-400">No apps assigned</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'status' as keyof AdminUser,
      header: 'Status',
      sortable: false,
      render: (value: any, user: AdminUser) => (
        <div className="flex items-center space-x-2">
          {user.isActive ? (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              <UserCheck className="h-3 w-3 mr-1" />
              Active
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
              <UserX className="h-3 w-3 mr-1" />
              Inactive
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'lastLoginAt' as keyof AdminUser,
      header: 'Last Login',
      sortable: true,
      render: (value: any, user: AdminUser) => (
        <span className="text-sm text-gray-500">
          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions' as keyof AdminUser,
      header: 'Actions',
      sortable: false,
      render: (value: any, user: AdminUser) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleView(user)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="View admin user details"
          >
            <Eye className="h-4 w-4" />
          </button>
          
          {/* Edit/Delete buttons - Only for users who can manage admin users */}
          {canManageAdminUsers && (
            <>
              <button
                onClick={() => handleEdit(user)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Edit admin user"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(user.id)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete admin user"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button onClick={() => fetchAdminUsers()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isSubAdmin ? 'Admin Users' : 'Admin Users Management'}
          </h1>
          <p className="text-gray-600">
            {isSubAdmin 
              ? 'You can only view your own profile' 
              : isSuperAdmin
                ? 'Manage platform customers (Admins) and their team members (Sub-Admins)'
                : 'Manage your team members (Sub-Admins) who will help manage your apps'
            }
          </p>
        </div>
                  {canCreateUsers && (
            <Button onClick={handleCreate}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add {isSuperAdmin ? 'Customer (Admin)' : 'Team Member (Sub-Admin)'}
            </Button>
          )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email..."
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none bg-white"
            >
              <option value="all">All Roles</option>
              {!isSubAdmin && <option value="super_admin">Super Admin</option>}
              {!isSubAdmin && <option value="admin">Admin</option>}
              <option value="sub_admin">Sub Admin</option>
            </select>
          </div>
          <div className="flex items-center justify-end">
            <Button onClick={() => fetchAdminUsers()} variant="outline">
              Refresh
            </Button>
          </div>
        </div>
      </div>



      {/* Admin Users Table */}
      <Table
        data={sortedUsers}
        columns={columns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        loading={isLoading}
        emptyMessage={
          isSubAdmin 
            ? "You don't have access to view other admin users"
            : canCreateUsers 
              ? "No admin users found."
              : "No admin users found"
        }
      />

      {/* Pagination Controls */}
      {sortedUsers.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => fetchAdminUsers(currentPage - 1)} 
            >
              Previous
            </Button>

            <Button
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => fetchAdminUsers(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* View Admin User Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedUser(null);
        }}
        title="Admin User Details"
        size="md"
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <span className="text-xl font-medium text-white">
                  {selectedUser.firstName?.charAt(0) || selectedUser.email.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedUser.firstName && selectedUser.lastName 
                    ? `${selectedUser.firstName} ${selectedUser.lastName}`
                    : selectedUser.firstName || 'N/A'
                  }
                </h3>
                <p className="text-gray-600">{selectedUser.email}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <p className="text-sm text-gray-900">{selectedUser.phone || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <p className="text-sm text-gray-900 capitalize">
                  {selectedUser.role === 'super_admin' ? 'Super Admin' : 
                   selectedUser.role === 'admin' ? 'Admin' : 'Sub Admin'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">
                  {selectedUser.isActive ? (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      <UserCheck className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                      <UserX className="h-3 w-3 mr-1" />
                      Inactive
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Login</label>
                <p className="text-sm text-gray-900">
                  {selectedUser.lastLoginAt ? new Date(selectedUser.lastLoginAt).toLocaleDateString() : 'Never'}
                </p>
              </div>
            </div>

            {selectedUser.permissions && selectedUser.permissions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                <div className="flex flex-wrap gap-2">
                  {selectedUser.permissions.map((permission) => {
                    const perm = availablePermissions.find(p => p.id === permission);
                    return (
                      <span key={permission} className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {perm?.label || permission}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action buttons - Only for users who can manage admin users */}
            {canManageAdminUsers && (
              <div className="flex space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsViewModalOpen(false);
                    handleEdit(selectedUser as AdminUser);
                  }}
                >
                  Edit Admin User
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleToggleActive((selectedUser as AdminUser).id, (selectedUser as AdminUser).isActive)}
                >
                  {(selectedUser as AdminUser).isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Create Admin User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          reset();
        }}
        title={isSuperAdmin ? "Create New Customer (Admin)" : "Create Team Member (Sub-Admin)"}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmitCreate)} className="space-y-4">
          {/* Hidden input to register flavourIds */}
          <input type="hidden" {...register('flavourIds')} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                {...register('firstName', { required: 'First name is required' })}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                {...register('lastName', { required: 'Last name is required' })}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              {...register('email', { required: 'Email is required' })}
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              {...register('phone')}
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              {...register('role', { required: 'Role is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            >
              <option value="">Select Role</option>
              {getAvailableRoles().map(role => (
                <option key={role} value={role}>
                  {role === 'admin' ? 'Customer (Admin)' : 'Team Member (Sub-Admin)'}
                </option>
              ))}
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          {/* App Assignment - Only for Admin and Sub-Admin users */}
          {(watchedRole === 'admin' || watchedRole === 'sub_admin') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Assign Apps
                </label>
                {availableApps.length > 0 && (
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        const allAppIds = availableApps.map(app => app.id);
                        setValue('flavourIds', allAppIds);
                      }}
                      className="text-xs text-primary hover:text-primary-700 underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setValue('flavourIds', [])}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50">
                {availableApps.length > 0 ? (
                  <div className="space-y-2">
                    {availableApps.map((app) => (
                      <label key={app.id} className="flex items-center space-x-3 p-2 hover:bg-white rounded cursor-pointer">
                        <input
                          type="checkbox"
                          value={app.id}
                          checked={watch('flavourIds')?.includes(app.id) || false}
                          onChange={(e) => {
                            const currentIds = watch('flavourIds') || [];
                            if (e.target.checked) {
                              setValue('flavourIds', [...currentIds, app.id]);
                            } else {
                              setValue('flavourIds', currentIds.filter(id => id !== app.id));
                            }
                          }}
                          className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{app.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{app.packageName}</div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {app.status === 'active' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                              {app.status}
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <Globe className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No apps available for assignment</p>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {watchedRole === 'admin' 
                  ? 'Check the apps you want to assign to this admin user. They will be able to see and manage all selected apps.'
                  : 'Check the apps you want to assign to this team member. They will be able to see and manage all selected apps.'
                }
              </p>
            </div>
          )}

          {/* Permissions - Only for Team Member (Sub-Admin) users */}
          {watchedRole === 'sub_admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Member Permissions
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {availablePermissions.map((permission) => (
                  <label key={permission.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={permission.id}
                      {...register('permissions')}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">{permission.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit">
              Create {isSuperAdmin ? 'Admin' : 'Sub Admin'} User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Admin User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
          reset();
        }}
        title="Edit Admin User"
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmitEdit)} className="space-y-4">
          {/* Hidden input to register flavourIds */}
          <input type="hidden" {...register('flavourIds')} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                {...register('firstName', { required: 'First name is required' })}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                {...register('lastName', { required: 'Last name is required' })}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              {...register('email', { required: 'Email is required' })}
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              {...register('phone')}
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              {...register('role', { required: 'Role is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              disabled={true} // Role cannot be changed after creation
            >
              <option value="admin">Admin</option>
              <option value="sub_admin">Sub Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Role cannot be changed after creation</p>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          {/* App Assignment - Only for Admin and Sub-Admin users */}
          {(selectedUser?.role === 'admin' || selectedUser?.role === 'sub_admin') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Assign Apps
                </label>
                {availableApps.length > 0 && (
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        const allAppIds = availableApps.map(app => app.id);
                        setValue('flavourIds', allAppIds);
                      }}
                      className="text-xs text-primary hover:text-primary-700 underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setValue('flavourIds', [])}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50">
                {availableApps.length > 0 ? (
                  <div className="space-y-2">
                    {availableApps.map((app) => (
                      <label key={app.id} className="flex items-center space-x-3 p-2 hover:bg-white rounded cursor-pointer">
                        <input
                          type="checkbox"
                          value={app.id}
                          checked={watch('flavourIds')?.includes(app.id) || false}
                          onChange={(e) => {
                            const currentIds = watch('flavourIds') || [];
                            if (e.target.checked) {
                              setValue('flavourIds', [...currentIds, app.id]);
                            } else {
                              setValue('flavourIds', currentIds.filter(id => id !== app.id));
                            }
                          }}
                          className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{app.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{app.packageName}</div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {app.status === 'active' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                              {app.status}
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <Globe className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No apps available for assignment</p>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {selectedUser?.role === 'admin' 
                  ? 'Check the apps you want to assign to this admin user. They will be able to see and manage all selected apps.'
                  : 'Check the apps you want to assign to this team member. They will be able to see and manage all selected apps.'
                }
              </p>
            </div>
          )}

          {/* Permissions - Only for Sub Admin users */}
          {selectedUser?.role === 'sub_admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {availablePermissions.map((permission) => (
                  <label key={permission.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={permission.id}
                      {...register('permissions')}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">{permission.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedUser(null);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit">
              Update Admin User
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminUsers; 